import { useCallback } from "react"
import { useSearchParams } from "react-router-dom"

type PageUpdater = number | ((prev: number) => number)

/**
 * Page index backed by a URL search param so it survives navigation, refresh, and link sharing.
 * Drop-in replacement for `useState(1)`. Page 1 is stored as the absence of the param, and updates
 * use history replace. Pass a distinct `paramName` when a route renders more than one list.
 */
export function usePageParam(paramName = "page"): [number, (next: PageUpdater) => void] {
  const [searchParams, setSearchParams] = useSearchParams()

  const parsed = Number(searchParams.get(paramName))
  const page = Number.isInteger(parsed) && parsed > 1 ? parsed : 1

  const setPage = useCallback(
    (next: PageUpdater) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          const current = Number(params.get(paramName)) || 1
          const value = typeof next === "function" ? next(current) : next
          if (value <= 1) params.delete(paramName)
          else params.set(paramName, String(value))
          return params
        },
        { replace: true },
      )
    },
    [paramName, setSearchParams],
  )

  return [page, setPage]
}
